/* ============================================================
   POUFUDIC Reviews Page 2026 — interaction layer
   - Loads pf-reviews-data.json
   - Filters (all / photos / 5★ / verified / rating-row)
   - Sort (recent / highest / photos-first)
   - Load more pagination
   - Photo lightbox (focus trap, keyboard nav)
   - Sticky CTA hide-on-scroll-down (mobile)
   - prefers-reduced-motion respected
   ============================================================ */
(function () {
  "use strict";

  if (window.__pfReviewsLoaded__) return;
  window.__pfReviewsLoaded__ = true;

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- Helpers ---------- */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

  function gradientFor(initial) {
    const palette = [
      ["#E8B488", "#C97D5A"],
      ["#7FB3A8", "#3F7569"],
      ["#D9A86B", "#B0743A"],
      ["#A28BBF", "#6B5A8A"],
      ["#9DB7C5", "#5A7E8E"],
      ["#C28B7B", "#8C5848"],
      ["#B3C7A0", "#6B855E"],
      ["#D9A8A8", "#9C5F5F"]
    ];
    const code = (initial || "?").charCodeAt(0);
    const pair = palette[code % palette.length];
    return `linear-gradient(135deg, ${pair[0]}, ${pair[1]})`;
  }

  function starSVG(filled) {
    return `<svg viewBox="0 0 14 14" focusable="false" aria-hidden="true" ${filled ? "" : 'class="is-empty"'}><path d="M7 1l1.7 3.95 4.3.4-3.25 2.95.95 4.2L7 10.4 3.3 12.5l.95-4.2L1 5.35l4.3-.4z"/></svg>`;
  }

  function escapeHtml(s) {
    return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  function timeAgo(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days < 1) return "today";
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
    if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? "" : "s"} ago`;
    if (days < 365) return `${Math.floor(days / 30)} month${days < 60 ? "" : "s"} ago`;
    return `${Math.floor(days / 365)} year${days < 730 ? "" : "s"} ago`;
  }

  /* ---------- Main class ---------- */
  class PFReviews {
    constructor(root) {
      this.root = root;
      this.dataUrl = root.getAttribute("data-pf-rv-data-url");
      this.perPage = parseInt(root.getAttribute("data-pf-rv-per-page"), 10) || 12;
      this.reviews = [];
      this.meta = {};
      this.filters = { tag: "all", rating: null };
      this.sort = "recent";
      this.visible = this.perPage;
      this.lightboxIndex = -1;
      this.lightboxPhotos = [];
      this._lastFocus = null;

      this._listEl = $("[data-pf-rv-list]", root);
      this._emptyEl = $("[data-pf-rv-empty]", root);
      this._loadWrap = $("[data-pf-rv-load-wrap]", root);
      this._loadBtn = $("[data-pf-rv-load-more]", root);
      this._photosBlock = $("[data-pf-rv-photos-block]", root);
      this._photosGrid = $("[data-pf-rv-photos-grid]", root);
      this._photosCount = $("[data-pf-rv-photos-count]", root);
      this._lightbox = $("[data-pf-rv-lightbox]", root);
      this._stickyCTA = $("[data-pf-rv-sticky]", root);

      this._bindEvents();
      this._loadData();
    }

    _bindEvents() {
      const root = this.root;

      // Filter chips
      $$("[data-pf-rv-filter]", root).forEach((btn) => {
        btn.addEventListener("click", () => this._setFilter(btn.getAttribute("data-pf-rv-filter")));
      });

      // Rating-row clicks (in distribution)
      $$("[data-pf-rv-filter-rating]", root).forEach((btn) => {
        btn.addEventListener("click", () => this._setRatingFilter(parseInt(btn.getAttribute("data-pf-rv-filter-rating"), 10)));
      });

      // Sort dropdown
      const sortWrap = $("[data-pf-rv-sort-wrap]", root);
      const sortToggle = $("[data-pf-rv-sort-toggle]", root);
      const sortLabel = $("[data-pf-rv-sort-label]", root);
      if (sortToggle) {
        sortToggle.addEventListener("click", () => {
          const open = sortWrap.getAttribute("data-open") === "true";
          sortWrap.setAttribute("data-open", open ? "false" : "true");
          sortToggle.setAttribute("aria-expanded", open ? "false" : "true");
        });
        $$("[data-pf-rv-sort]", root).forEach((opt) => {
          opt.addEventListener("click", () => {
            this.sort = opt.getAttribute("data-pf-rv-sort");
            $$("[data-pf-rv-sort]", root).forEach((o) => o.setAttribute("aria-current", "false"));
            opt.setAttribute("aria-current", "true");
            if (sortLabel) sortLabel.textContent = opt.textContent;
            sortWrap.setAttribute("data-open", "false");
            sortToggle.setAttribute("aria-expanded", "false");
            this._render();
          });
        });
        document.addEventListener("click", (e) => {
          if (!sortWrap.contains(e.target)) {
            sortWrap.setAttribute("data-open", "false");
            sortToggle.setAttribute("aria-expanded", "false");
          }
        });
      }

      // Load more
      if (this._loadBtn) {
        this._loadBtn.addEventListener("click", () => {
          this.visible += this.perPage;
          this._render();
        });
      }

      // Reset filters
      const reset = $("[data-pf-rv-reset]", root);
      if (reset) reset.addEventListener("click", () => this._resetFilters());

      // Lightbox events
      if (this._lightbox) {
        $("[data-pf-rv-lb-close]", root).addEventListener("click", () => this._closeLightbox());
        $("[data-pf-rv-lb-prev]", root).addEventListener("click", () => this._navLightbox(-1));
        $("[data-pf-rv-lb-next]", root).addEventListener("click", () => this._navLightbox(1));
        this._lightbox.addEventListener("click", (e) => {
          if (e.target === this._lightbox) this._closeLightbox();
        });
      }

      // Keyboard
      document.addEventListener("keydown", (e) => {
        if (!this._lightbox || this._lightbox.getAttribute("data-open") !== "true") return;
        if (e.key === "Escape") this._closeLightbox();
        else if (e.key === "ArrowLeft") this._navLightbox(-1);
        else if (e.key === "ArrowRight") this._navLightbox(1);
        else if (e.key === "Tab") this._trapFocus(e);
      });

      // Sticky CTA hide-on-scroll-down
      if (this._stickyCTA) {
        let lastY = window.scrollY;
        window.addEventListener(
          "scroll",
          () => {
            const y = window.scrollY;
            if (y > lastY && y > 200) this._stickyCTA.setAttribute("data-hidden", "true");
            else this._stickyCTA.setAttribute("data-hidden", "false");
            lastY = y;
          },
          { passive: true }
        );
      }
    }

    async _loadData() {
      try {
        const res = await fetch(this.dataUrl, { cache: "default" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.meta = data.meta || {};
        this.reviews = (data.reviews || []).slice();
        this.reviews.forEach(function (r) { if (!Array.isArray(r.photos)) r.photos = r.photos ? [r.photos] : []; });
        this._updateStats();
        this._renderPhotos();
        this._render();
      } catch (err) {
        console.warn("[pf-reviews] failed to load data:", err);
        this._listEl.innerHTML = `<li class="pf-rv__card"><p class="pf-rv__card-text">Reviews are loading. Please refresh in a moment.</p></li>`;
      }
    }

    _updateStats() {
      const meta = this.meta;
      $$("[data-pf-rv-stat]", this.root).forEach((el) => {
        const k = el.getAttribute("data-pf-rv-stat");
        if (meta[k] != null) el.textContent = fmt(meta[k]);
      });
      $$("[data-pf-rv-stat-num]", this.root).forEach((el) => {
        const k = el.getAttribute("data-pf-rv-stat-num");
        if (meta[k] != null) el.textContent = meta[k];
      });
      // Distribution bars
      const dist = meta.distribution || {};
      const total = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
      [1, 2, 3, 4, 5].forEach((n) => {
        const fillEl = this.root.querySelector(`[data-pf-rv-dist="${n}"]`);
        const cntEl = this.root.querySelector(`[data-pf-rv-dist-count="${n}"]`);
        const count = dist[n] || 0;
        const ratio = count / total;
        if (fillEl) fillEl.style.setProperty("--pf-fill", ratio.toFixed(3));
        if (cntEl) cntEl.textContent = count.toLocaleString();
      });
    }

    _renderPhotos() {
      if (!this._photosGrid) return;

      // Real Etsy customer-photo gallery (meta.customerPhotos) takes priority when present.
      // Shown as an honest gallery — real photos, generic "Verified Etsy buyer" caption, no fabricated name/text.
      const gallery = Array.isArray(this.meta.customerPhotos) ? this.meta.customerPhotos.filter(Boolean) : [];
      if (gallery.length > 0) {
        this._photosBlock.removeAttribute("hidden");
        this._photosCount.textContent = `${gallery.length} customer photo${gallery.length === 1 ? "" : "s"}`;
        const galReviews = gallery.slice(0, 48).map((url) => ({ photos: [url], name: "Verified Etsy buyer", country: "Verified Etsy Buyer", city: "", title: "", text: "", rating: 5, verified: true, productHandle: "" }));
        this._photosGrid.innerHTML = galReviews.map((r, i) =>
          `<button class="pf-rv__photo" type="button" role="listitem" data-pf-rv-gal="${i}" aria-label="Customer photo">
            <img src="${escapeHtml(r.photos[0])}" alt="" loading="lazy" decoding="async">
          </button>`).join("");
        $$("[data-pf-rv-gal]", this._photosGrid).forEach((btn) => {
          btn.addEventListener("click", () => {
            const idx = parseInt(btn.getAttribute("data-pf-rv-gal"), 10);
            if (idx > -1) this._openLightbox(galReviews, idx);
          });
        });
        return;
      }

      const photoReviews = this.reviews.filter((r) => r.photos && r.photos.length > 0);
      if (photoReviews.length === 0) return;

      this._photosBlock.removeAttribute("hidden");
      this._photosCount.textContent = `${photoReviews.length} review${photoReviews.length === 1 ? "" : "s"} with photos`;

      const html = photoReviews.slice(0, 16).map((r) => {
        const photo = r.photos[0];
        return `<button class="pf-rv__photo" type="button" role="listitem" data-pf-rv-open-photo="${r.id}" aria-label="Photo from ${escapeHtml(r.name)}'s review">
          ${photo
            ? `<img src="${escapeHtml(photo)}" alt="" loading="lazy" decoding="async">`
            : `<span class="pf-rv__photo-placeholder">Photo</span>`}
        </button>`;
      }).join("");
      this._photosGrid.innerHTML = html;

      $$("[data-pf-rv-open-photo]", this._photosGrid).forEach((btn) => {
        btn.addEventListener("click", () => {
          const id = btn.getAttribute("data-pf-rv-open-photo");
          const idx = photoReviews.findIndex((r) => r.id === id);
          if (idx > -1) this._openLightbox(photoReviews, idx);
        });
      });
    }

    _setFilter(tag) {
      this.filters.tag = tag;
      $$("[data-pf-rv-filter]", this.root).forEach((b) => b.setAttribute("aria-pressed", b.getAttribute("data-pf-rv-filter") === tag ? "true" : "false"));
      this.visible = this.perPage;
      this._render();
    }

    _setRatingFilter(n) {
      const current = this.filters.rating;
      this.filters.rating = current === n ? null : n;
      $$("[data-pf-rv-filter-rating]", this.root).forEach((b) => b.setAttribute("aria-pressed", parseInt(b.getAttribute("data-pf-rv-filter-rating"), 10) === this.filters.rating ? "true" : "false"));
      this.visible = this.perPage;
      this._render();
    }

    _resetFilters() {
      this.filters = { tag: "all", rating: null };
      this.sort = "recent";
      this.visible = this.perPage;
      $$("[data-pf-rv-filter]", this.root).forEach((b) => b.setAttribute("aria-pressed", b.getAttribute("data-pf-rv-filter") === "all" ? "true" : "false"));
      $$("[data-pf-rv-filter-rating]", this.root).forEach((b) => b.setAttribute("aria-pressed", "false"));
      $$("[data-pf-rv-sort]", this.root).forEach((o) => o.setAttribute("aria-current", o.getAttribute("data-pf-rv-sort") === "recent" ? "true" : "false"));
      this._render();
    }

    _applyFilters() {
      let list = this.reviews.slice();
      if (this.filters.tag === "photos") list = list.filter((r) => r.photos && r.photos.length > 0);
      else if (this.filters.tag === "5star") list = list.filter((r) => r.rating === 5);
      else if (this.filters.tag === "verified") list = list.filter((r) => r.verified);
      if (this.filters.rating != null) list = list.filter((r) => r.rating === this.filters.rating);

      if (this.sort === "recent") {
        list.sort((a, b) => new Date(b.date) - new Date(a.date));
      } else if (this.sort === "highest") {
        list.sort((a, b) => b.rating - a.rating || new Date(b.date) - new Date(a.date));
      } else if (this.sort === "photos") {
        list.sort((a, b) => (b.photos.length > 0 ? 1 : 0) - (a.photos.length > 0 ? 1 : 0) || new Date(b.date) - new Date(a.date));
      }
      return list;
    }

    _render() {
      const all = this._applyFilters();
      const visible = all.slice(0, this.visible);
      const hasMore = this.visible < all.length;
      this._listEl.setAttribute("aria-busy", "true");

      if (visible.length === 0) {
        this._listEl.innerHTML = "";
        this._listEl.setAttribute("data-empty", "true");
        this._emptyEl.setAttribute("data-visible", "true");
        this._loadWrap.style.display = "none";
      } else {
        this._listEl.removeAttribute("data-empty");
        this._emptyEl.removeAttribute("data-visible");
        this._listEl.innerHTML = visible.map((r) => this._cardHTML(r)).join("");
        this._loadWrap.style.display = hasMore ? "block" : "none";
      }

      this._listEl.setAttribute("aria-busy", "false");

      // Wire up photo clicks
      $$("[data-pf-rv-open-card-photo]", this._listEl).forEach((btn) => {
        btn.addEventListener("click", () => {
          const rid = btn.getAttribute("data-pf-rv-rid");
          const pidx = parseInt(btn.getAttribute("data-pf-rv-pidx"), 10);
          const collection = this.reviews.filter((r) => r.photos && r.photos.length > 0);
          const start = collection.findIndex((r) => r.id === rid);
          this._openLightbox(collection, Math.max(0, start), pidx);
        });
      });
    }

    _cardHTML(r) {
      const stars = Array.from({ length: 5 }, (_, i) => starSVG(i < r.rating)).join("");
      const photos = (r.photos || []).slice(0, 4).map((p, i) => `
        <button class="pf-rv__card-photo" type="button" data-pf-rv-open-card-photo data-pf-rv-rid="${r.id}" data-pf-rv-pidx="${i}" aria-label="View photo ${i + 1}">
          <img src="${escapeHtml(p)}" alt="" loading="lazy" decoding="async">
        </button>
      `).join("");
      const verified = r.verified
        ? `<span class="pf-rv__verified-pill"><svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 6.5l2 2 4-4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>VERIFIED BUYER</span>`
        : "";
      const place = [r.city, r.country].filter(Boolean).join(", ");
      return `
        <li class="pf-rv__card" itemscope itemtype="https://schema.org/Review">
          <div class="pf-rv__card-eyebrow">
            ${verified}
            ${place ? `<span class="pf-rv__dot"></span><span>${escapeHtml(place)}</span>` : ""}
          </div>
          <div class="pf-rv__card-header">
            <div class="pf-rv__avatar" aria-hidden="true" style="--pf-g:${gradientFor(r.initial || r.name)}">${escapeHtml((r.initial || (r.name || "?").charAt(0)).toUpperCase())}</div>
            <div class="pf-rv__author">
              <span class="pf-rv__author-name" itemprop="author">${escapeHtml(r.name || "Customer")}</span>
              <span class="pf-rv__author-meta">
                <time datetime="${escapeHtml(r.date || "")}" itemprop="datePublished">${escapeHtml(timeAgo(r.date))}</time>
              </span>
              <span class="pf-rv__stars" itemprop="reviewRating" itemscope itemtype="https://schema.org/Rating" aria-label="Rated ${r.rating} of 5">${stars}<meta itemprop="ratingValue" content="${r.rating}"><meta itemprop="bestRating" content="5"></span>
            </div>
          </div>
          ${r.title ? `<h3 class="pf-rv__card-title" itemprop="name">${escapeHtml(r.title)}</h3>` : ""}
          <p class="pf-rv__card-text" itemprop="reviewBody">${escapeHtml(r.text || "")}</p>
          ${r.productHandle ? `<a class="pf-rv__card-product" href="/products/${escapeHtml(r.productHandle)}" itemprop="itemReviewed">${escapeHtml(r.product || "View product")}</a>` : ""}
          ${photos ? `<div class="pf-rv__card-photos">${photos}</div>` : ""}
        </li>
      `;
    }

    _openLightbox(list, index, photoIdx) {
      if (!this._lightbox) return;
      this.lightboxList = list;
      this.lightboxIndex = index;
      this.lightboxPhotoIdx = photoIdx || 0;
      this._lastFocus = document.activeElement;
      this._fillLightbox();
      this._lightbox.setAttribute("data-open", "true");
      document.body.style.overflow = "hidden";
      setTimeout(() => this._lightbox.focus(), 10);
    }

    _closeLightbox() {
      if (!this._lightbox) return;
      this._lightbox.setAttribute("data-open", "false");
      document.body.style.overflow = "";
      if (this._lastFocus && typeof this._lastFocus.focus === "function") {
        setTimeout(() => this._lastFocus.focus(), 0);
      }
    }

    _navLightbox(dir) {
      if (!this.lightboxList) return;
      this.lightboxIndex = (this.lightboxIndex + dir + this.lightboxList.length) % this.lightboxList.length;
      this.lightboxPhotoIdx = 0;
      this._fillLightbox();
    }

    _fillLightbox() {
      const r = this.lightboxList[this.lightboxIndex];
      if (!r) return;
      const img = $("[data-pf-rv-lb-img]", this.root);
      const photo = r.photos[this.lightboxPhotoIdx] || r.photos[0];
      img.src = photo || "";
      img.alt = r.title || r.text || "";

      $("[data-pf-rv-lb-eyebrow]", this.root).innerHTML = r.verified
        ? `<span class="pf-rv__verified-pill"><svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 6.5l2 2 4-4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>VERIFIED BUYER</span>` +
            (r.city || r.country ? `<span class="pf-rv__dot"></span><span>${escapeHtml([r.city, r.country].filter(Boolean).join(", "))}</span>` : "")
        : "";
      const avatar = $("[data-pf-rv-lb-avatar]", this.root);
      avatar.textContent = (r.initial || (r.name || "?").charAt(0)).toUpperCase();
      avatar.style.setProperty("--pf-g", gradientFor(r.initial || r.name));
      $("[data-pf-rv-lb-name]", this.root).textContent = r.name || "Customer";
      $("[data-pf-rv-lb-meta]", this.root).textContent = timeAgo(r.date);
      const starsHTML = Array.from({ length: 5 }, (_, i) => starSVG(i < r.rating)).join("");
      $("[data-pf-rv-lb-stars]", this.root).innerHTML = starsHTML;
      $("[data-pf-rv-lb-title]", this.root).textContent = r.title || "";
      $("[data-pf-rv-lb-text]", this.root).textContent = r.text || "";
      const productLink = $("[data-pf-rv-lb-product]", this.root);
      if (r.productHandle) {
        productLink.href = `/products/${r.productHandle}`;
        productLink.textContent = r.product || "View product";
        productLink.style.display = "inline-flex";
      } else {
        productLink.style.display = "none";
      }
    }

    _trapFocus(e) {
      if (!this._lightbox) return;
      const focusables = this._lightbox.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function initAll() {
    document.querySelectorAll("[data-pf-rv-root]").forEach((root) => {
      if (root.__pfRvInit) return;
      root.__pfRvInit = true;
      new PFReviews(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
})();
